import { usePermission } from '@backstage/plugin-permission-react';
import { policyEntityCreatePermission } from '@backstage-community/plugin-rbac-common';
import {
  pagerDutyAdminPermission,
  feedbackReadAllPermission,
  announcementCreatePermission,
  announcementUpdatePermission,
  announcementDeletePermission,
  groupSlackChannelsPermission,
  recommendedDocsManagePermission,
} from '@internal/hone-common';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@material-ui/core';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import ThumbsUpDownIcon from '@material-ui/icons/ThumbsUpDown';
import ForumIcon from '@material-ui/icons/Forum';
import LibraryAddCheckIcon from '@material-ui/icons/LibraryAddCheck';

export type AdminSection = {
  title: string;
  to: string;
  icon: ComponentType<SvgIconProps>;
  description: string;
};

export const useAdminSections = (): {
  sections: AdminSection[];
  loading: boolean;
} => {
  const rbac = usePermission({ permission: policyEntityCreatePermission });
  const pagerduty = usePermission({ permission: pagerDutyAdminPermission });
  const announcementCreate = usePermission({
    permission: announcementCreatePermission,
  });
  const announcementUpdate = usePermission({
    permission: announcementUpdatePermission,
  });
  const announcementDelete = usePermission({
    permission: announcementDeletePermission,
  });
  const feedback = usePermission({ permission: feedbackReadAllPermission });
  const slackChannels = usePermission({
    permission: groupSlackChannelsPermission,
  });
  const recommendedDocs = usePermission({
    permission: recommendedDocsManagePermission,
  });

  const loading =
    rbac.loading ||
    pagerduty.loading ||
    announcementCreate.loading ||
    announcementUpdate.loading ||
    announcementDelete.loading ||
    feedback.loading ||
    slackChannels.loading ||
    recommendedDocs.loading;

  const sections: AdminSection[] = [];

  if (rbac.allowed) {
    sections.push({
      title: 'RBAC',
      to: '/rbac',
      icon: VpnKeyIcon,
      description:
        'Manage roles, permissions, and their bindings to users and groups.',
    });
  }
  if (pagerduty.allowed) {
    sections.push({
      title: 'PagerDuty',
      to: '/pagerduty',
      icon: NotificationsActiveIcon,
      description:
        'Configure service mapping, custom fields, and account settings.',
    });
  }
  if (
    announcementCreate.allowed ||
    announcementUpdate.allowed ||
    announcementDelete.allowed
  ) {
    sections.push({
      title: 'Announcements',
      to: '/announcements/admin',
      icon: AnnouncementIcon,
      description:
        'Create, edit, and delete announcements, categories, and tags.'
    });
  }
  if (feedback.allowed) {
    sections.push({
      title: 'All Feedback',
      to: '/admin/feedback',
      icon: ThumbsUpDownIcon,
      description:
        'Review like/dislike feedback and responses across every entity.',
    });
  }
  if (slackChannels.allowed) {
    sections.push({
      title: 'Slack Channels',
      to: '/admin/slack-channels',
      icon: ForumIcon,
      description:
        'Map catalog groups to the Slack channel their notifications post to.',
    });
  }
  if (recommendedDocs.allowed) {
    sections.push({
      title: 'Recommended Documentation',
      to: '/admin/recommended-docs',
      icon: LibraryAddCheckIcon,
      description:
        'Curate the company and per-team docs featured on the Documentation landing page.',
    });
  }

  return { sections, loading };
};
